import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import * as moment from 'moment';
import FeatureModel from 'src/app/core/models/feature.model';
import { FeatureService } from 'src/app/core/services/feature.service';
import FunctionModel from 'src/app/core/models/function.model';
import { FunctionService } from 'src/app/core/services/function.service';
import { RoutesService } from 'src/app/core/services/routes.service';
import ScreenModel from 'src/app/core/models/screen.model';
import { ScreenService } from 'src/app/core/services/screen.service';
import RoutesModel, { IFeature, IFunction, IProduct, IScreen } from 'src/app/core/models/route.model';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { BatchLoader } from "../../../core/utilities/batch-loader.util";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

declare var $: any;

/**
 * @fileOverview RoutesComponent
 * @module AdminPanel/UserAccess/routes
 * @description Manages the creation, update, and listing of application routes with batch-loaded pagination.
 * Routes define relationships between products, microservices, screens, features, and functions,
 * and map them to URLs for navigation.
 */

/**
 * @component RoutesComponent
 * @selector app-routes
 * @templateUrl ./routes.component.html
 * @styleUrls ['./routes.component.scss']
 * @implements {OnInit, OnDestroy}
 * @description Reactive form component for application route management (CRUD operations).
 * 
 * **Core Responsibilities:**
 * 1. Display batch-loaded list of application routes (12 items per batch) with infinite scroll
 * 2. Provide reactive form for creating new application routes
 * 3. Enable edit mode to update existing routes
 * 4. Search and filter routes by feature name and product name with character normalization
 * 5. Manage hierarchical relationships: Product → Feature → Screen/Function → URL mapping
 * 6. Format timestamps for display (DD-MMM-YYYY hh:mm:ss A)
 * 7. Handle form validation for all required route fields
 * 8. Prevent double-click submissions
 * 9. Clean up resources and cancel HTTP requests on component destruction
 * 
 * **Data Flow:**
 * - Fetch all master data (routes, features, screens, functions, products) on component init
 * - Initialize batch loader with 12-item batches
 * - Load first batch and display in template
 * - On scroll near bottom, automatically load next batch
 * - On search, filter routes and update pagination
 * 
 * **Form Fields:**
 * - `product` (string): Product master data selection, required
 * - `microService` (string): Microservice identifier, required
 * - `screen` (string): Screen master data selection, required
 * - `feature` (string): Feature master data selection, required
 * - `function` (string): Function master data selection, required
 * - `status` (string): Route status, required
 * - `url` (string): Navigation URL, required
 * 
 * **Route Object Structure:**
 * Routes contain hierarchical feature data:
 * ```
 * route.feature[0].name (Feature)
 * route.feature[0].product[0].name (Product)
 * route.feature[0].screen[0].name (Screen)
 * route.feature[0].function[0].name (Function)
 * ```
 * 
 * **Features:**
 * - Create mode: Submit new route with empty form
 * - Edit mode: Update existing route from list
 * - Batch loader: 12 items per batch, infinite scroll
 * - Search: Case-insensitive by feature name or product name
 * - Character normalization: Removes spaces and hyphens for matching
 * - Timestamp formatting: DD-MMM-YYYY hh:mm:ss A format
 * - Double-click prevention: Disables buttons during submission
 * - Sort functionality: Sortable by any key, toggleable direction
 * 
 * **Dependencies:**
 * - RoutesService: CRUD operations for application routes
 * - FeatureService: Fetch feature master data
 * - ScreenService: Fetch screen master data
 * - FunctionService: Fetch function master data
 * - ToastrService: Toast notifications for user feedback
 * - CommonMethodsService: Shared utility methods
 * - BatchLoader: Pagination utility for infinite scroll
 */
@Component({
  selector: 'app-routes',
  templateUrl: './routes.component.html',
  styleUrls: ['./routes.component.scss']
})
export class RoutesComponent implements OnInit, OnDestroy {
  /** @property routes @type {RoutesModel[]} @description Complete list of all application routes fetched from service @public */
  public routes: RoutesModel[] = [];

  /** @property routesList @type {RoutesModel[]} @description Currently displayed batch of routes from batch loader @public */
  public routesList: RoutesModel[] = [];

  /** @property route @type {RoutesModel} @description Current route object for form binding (create/edit mode) @public */
  public route: RoutesModel = new RoutesModel();

  /** @property filteredRoutes @type {RoutesModel[]} @description Filtered list of routes from search query @public */
  public filteredRoutes: RoutesModel[] = [];

  /** @property features @type {Array<FeatureModel>} @description Master data of all available features for dropdown @public */
  public features: Array<FeatureModel>;

  /** @property screens @type {Array<ScreenModel>} @description Master data of all available screens for dropdown @public */
  public screens: Array<ScreenModel>;

  /** @property functions @type {Array<FunctionModel>} @description Master data of all available functions for dropdown @public */
  public functions: Array<FunctionModel>;

  /** @property function @type {IFunction} @description Current function object reference for route @public */
  public function: IFunction;

  /** @property feature @type {IFeature} @description Current feature object reference for route @public */
  public feature: IFeature;

  /** @property product @type {IProduct} @description Current product object reference for route @public */
  public product: IProduct;

  /** @property screen @type {IScreen} @description Current screen object reference for route @public */
  public screen: IScreen;

  /** @property loginPersonType @type {string} @description User's role type from localStorage (EMPLOYEE, ADMIN, etc) @public */
  public loginPersonType: string;

  /** @property LoginUserName @type {string} @description Logged-in user's name from localStorage @public */
  public LoginUserName: string;

  /** @property loginUserBusinessName @type {string} @description Logged-in user's business name from localStorage @public */
  public loginUserBusinessName: string;

  /** @property search @type {string} @description Search query string for filtering routes @public */
  public search: string;

  /** @property submitButton @type {boolean} @description Flag to determine if form is in create mode (submit button visible) @public */
  public submitButton: boolean;

  /** @property updateButton @type {boolean} @description Flag to determine if form is in edit mode (update button visible) @public */
  public updateButton: boolean;

  /** @property key @type {string} @description Sort key for table sorting (default: 'name') @public */
  key: string = 'name';

  /** @property reverse @type {boolean} @description Sort direction flag (false=ascending, true=descending) @public */
  reverse: boolean = false;

  /** @property responseLoader @type {boolean} @description Page-level loader flag for initial data load @public */
  responseLoader: boolean;

  /**
   * @method sort
   * @description Toggles sort direction for specified key in table sorting.
   * Updates key and reverses sort order.
   * 
   * @public
   * @param {string} key - The property key to sort by
   * @returns {void}
   */
  sort(key: string) {
    this.key = key;
    this.reverse = !this.reverse;
  }

  /** @property routesDoubleClick @type {boolean} @description Flag to prevent double-click submission on form @public */
  public routesDoubleClick: boolean;

  /** @property NoDataMessage @type {boolean} @description Flag to display "no data" message when list is empty @public */
  public NoDataMessage: boolean = false;

  /** @property activePageNo @type {number} @description Current active page number for pagination @public */
  public activePageNo: number;

  /** @property clearSearchInput @type {boolean} @description Flag to clear search input field from template @public */
  public clearSearchInput: boolean;

  /** @property listLoader @type {boolean} @description Loading spinner flag for batch loader (shows during pagination load) @public */
  public listLoader = false;

  /** @property startIndex @type {number} @description Tracks how many items have been loaded in pagination @public */
  public startIndex = 0;

  /** @property isFiltering @type {boolean} @description Flag indicating if search filter is active @public */
  public isFiltering: boolean = false;

  /** @property noList @type {boolean} @description Flag indicating if current list is empty @public */
  public noList: boolean = false;

  /** @property scrollTimeout @type {any} @description Timeout ID for debounced scroll handler (50ms) @private */
  private scrollTimeout: any;

  /** @property searchTimeout @type {any} @description Timeout ID for debounced search handler @public */
  searchTimeout: any;

  /** @property formFieldCount @type {number} @description Total number of form fields (used for layout calculation) @public */
  public formFieldCount: number = 0;

  /** @property showForm @type {boolean} @description Flag to toggle form visibility on UI @public */
  public showForm: boolean = false;

  /** @property registerForm @type {FormGroup} @description Reactive form group for route creation/update with validation rules @public */
  public registerForm: FormGroup;

  /** @property formSubmitted @type {boolean} @description Flag to track if form has been submitted (for validation error display) @public */
  public formSubmitted: boolean = false;


  /**
   * @constructor
   * @param {FeatureService} featureService - Feature master data service
   * @param {ScreenService} screenService - Screen master data service
   * @param {Sidenavaction} sidenavaction - Navigation menu controller service
   * @param {RoutesService} routeService - Route CRUD service
   * @param {FunctionService} functionService - Function master data service
   * @param {CommonMethodsService} commonMethodsService - Shared utility methods service
   * @param {LocationStrategy} locationStrategy - Location tracking for breadcrumb
   * @param {PreviousRouteService} previousRouteService - Previous route tracking service
   * @param {FormBuilder} formBuilder - Angular reactive form builder
   * @param {ToastrService} toasterService - Toast notification service for user feedback
   * @public
   */
  constructor(public featureService: FeatureService, public screenService: ScreenService, public sidenavaction: Sidenavaction, public routeService: RoutesService, public functionService: FunctionService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, public formBuilder: FormBuilder, public toasterService: ToastrService
  ) {
    this.route = new RoutesModel();
    this.feature = new IFeature();
    this.product = new IProduct();
    this.screen = new IScreen();
    this.function = new IFunction();

    this.registerForm = this.formBuilder.group({
      product: ['', Validators.required],
      microService: ['', Validators.required],
      screen: ['', Validators.required],
      feature: ['', Validators.required],
      function: ['', Validators.required],
      status: ['', Validators.required],
      url: ['', Validators.required]
    })

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /** @property batchLoader @type {BatchLoader<any>} @description Pagination utility for batch-loaded infinite scroll @private */
  private batchLoader: BatchLoader<any>;

  /**
   * @method ngOnInit
   * @async
   * @description Angular lifecycle hook. Initializes component on load with:
   * 1. Scroll page to top
   * 2. Reset all component state and lists
   * 3. Load user context from localStorage (role, name, business)
   * 4. Fetch all master data (routes, functions, screens, features)
   * 5. Format timestamps for display
   * 6. Initialize batch loader and load first batch
   * 
   * @public
   * @async
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.closeForm();
      this.routes = [];
      this.routesList = [];
      this.isFiltering = false;
      this.responseLoader = false;
      this.search = '';
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.routesDoubleClick = false;
      this.submitButton = false;
      this.updateButton = false;
      /*users data*/
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.routes = await this.routeService.getAllRoutes();
      this.functions = await this.functionService.getAllFunctions();
      this.screens = await this.screenService.getAllScreens();
      this.features = await this.featureService.getAllFeatures();
      for (let i = 0; i < this.routes.length; i++) {
        if (this.routes[i].updatedAt) {
          const timeFormat = moment(this.routes[i].updatedAt).format('DD-MMM-YYYY hh:mm:ss A');
          this.routes[i].updatedAt = JSON.parse(JSON.stringify(timeFormat));
        }
      }
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.responseLoader = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.responseLoader = true;
    }
  }

  /**
   * @property f
   * @getter
   * @description Getter for convenient access to form controls in template.
   * 
   * @public
   * @type {AbstractControl}
   * @returns {any} Form controls object
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Detects when user scrolls near
   * the bottom and triggers loading of next batch (50ms debounce).
   * 
   * @public
   * @hostListener window:scroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @method checkScrollAndLoad
   * @description Checks if user scrolled near bottom and loads next batch with 50ms debounce.
   * Skips loading if already loading or if in filtering mode.
   * 
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @description Initializes batch loader for pagination (12 items per batch).
   * Binds loader to routes list and loading state.
   * 
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.routes,
      (updatedList) => {
        this.routesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method applySearch
   * @description Applies search filter to routes by feature name and product name.
   * Removes spaces and hyphens for character-normalized matching.
   * Resets pagination and loads filtered results.
   * 
   * @public
   * @returns {void}
   */
  public applySearch(): void {
    this.routesList = [];
    const query = this.search?.replace(/[-_]|\s/g, "").trim().toLowerCase();
    this.isFiltering = query.length > 0;

    if (this.isFiltering) {
      this.routesList = this.routes.filter(route =>
        route.feature?.[0]?.name?.replace(/[-_]|\s/g, "").toLowerCase().includes(query) ||
        route.feature?.[0]?.product?.[0]?.name?.replace(/[-_]|\s/g, "").toLowerCase().includes(query)
      )
    } else {
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);
    }

    this.noList = this.routesList.length === 0;
  }

  /**
   * @method addUpdateRoute
   * @async
   * @description Creates new route or updates existing route. Validates form before submission,
   * constructs hierarchical feature object with product/screen/function references,
   * prevents double-click, and reloads route list after successful operation.
   * 
   * **Create Mode:** Submits new route with all selected components.
   * **Edit Mode:** Updates existing route without submitting ID, clones object for safety.
   * 
   * **Route Structure Created:**
   * ```
   * route.feature[0].name = feature name
   * route.feature[0].product[0].name = product name
   * route.feature[0].screen[0].name = screen name
   * route.feature[0].function[0].name = function name
   * ```
   * 
   * @public
   * @async
   * @returns {Promise<void | boolean>}
   */
  /*get all featuere end*/

  public async addUpdateRoute() {
    this.formSubmitted = true;
    if (this.registerForm.invalid) return false;
    this.formSubmitted = false;
    this.route.feature = [{
      name: this.feature.name,
      function: [{
        name: this.function.name
      }],
      screen: [{
        name: this.screen.name
      }],
      product: [{
        name: this.product.name
      }]
    }]
    try {
      this.routesDoubleClick = true;
      if (this.submitButton) {
        this.route = await this.routeService.create(this.route);
        this.toasterService.success('Route added successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateRoutes = Object.assign({}, this.route);
        delete updateRoutes.id;
        this.route = await this.routeService.updateOneByID(this.route.id, updateRoutes);
        this.toasterService.success('Route updated successfully', '', {
          timeOut: 2000
        });
      }
      this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.routesDoubleClick = false;
    }
  }

  /**
   * @method getRouteById
   * @async
   * @description Retrieves route by ID from current list and populates reactive form with data.
   * Extracts hierarchical data (feature, product, screen, function) from route object.
   * Used for edit mode to pre-fill form with existing route values.
   * 
   * @public
   * @async
   * @param {string} id - The unique identifier of route to retrieve
   * @returns {void}
   */
  /*get by feature by id*/
  public async getRouteById(id: string) {
    this.routesDoubleClick = false;
    this.showForm = true;
    this.updateButton = true;
    this.submitButton = false;
    const foundRoute = this.routesList.find(route => route?.id === id);
    this.route = foundRoute ? { ...foundRoute } : new RoutesModel(); // clone object
    this.function.name = this.route.feature[0].function[0].name;
    this.feature.name = this.route.feature[0].name;
    this.screen.name = this.route.feature[0].screen[0].name;
    this.product.name = this.route.feature[0].product[0].name;
  }

  /**
   * @method openForm
   * @description Initializes create mode. Sets form state flags to show submit button,
   * displays form for new route creation.
   * 
   * @public
   * @returns {void}
   */
  /*handle update and submit buttons*/
  public openForm() {
    this.submitButton = true;
    this.updateButton = false;
    this.showForm = true;
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @method closeForm
   * @description Closes route form and resets all form-related state. Called on Cancel button
   * or after successful submission. Clears all route component references.
   * 
   * @public
   * @returns {void}
   */
  /*click on cancel button form data empty reload the page*/
  public closeForm() {
    this.registerForm.reset();
    this.showForm = false;
    this.route = new RoutesModel();
    this.feature = new IFeature();
    this.product = new IProduct();
    this.screen = new IScreen();
    this.function = new IFunction();
  }

  /**
   * @method ngOnDestroy
   * @description Angular lifecycle hook. Cancels any ongoing HTTP requests from all services
   * (routes, functions, screens, features), marks batch loader as destroyed,
   * and resets batch loader state to prevent memory leaks.
   * 
   * @public
   * @returns {void}
   */
  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.routeService.cancelOnGoingHttpRequests();
    this.functionService.cancelOnGoingHttpRequests();
    this.screenService.cancelOnGoingHttpRequests();
    this.featureService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }
}

